import { booleanAttribute, Component, inject, Input } from '@angular/core';
import { ToastService } from '@cnt-workspace/data-access';

/**
 * Copies a value (an email, a phone number, a booking code) and says so in a
 * toast. Icon-only by default, labelled for screen readers ("Copy Gus Guest's
 * phone"); `text` shows "Copy <value>" beside the icon instead.
 */
@Component({
  selector: 'admin-copy-button',
  standalone: true,
  template: `
    <button type="button" [class.text]="text" [title]="value" [attr.aria-label]="label" (click)="copy()">
      <span class="material-symbols-outlined" aria-hidden="true">{{ icon }}</span>
      @if (text) {
        <span>Copy {{ value }}</span>
      }
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
      button {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px;
        border: 0;
        border-radius: 6px;
        background: none;
        color: var(--cnt-muted);
        font: inherit;
        cursor: pointer;
      }
      button:hover {
        color: var(--cnt-green);
      }
      button:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 1px;
      }
      .material-symbols-outlined {
        font-size: 16px;
      }
      /* With its words: a small outlined pill, e.g. "Copy r-185" by a status chip. */
      button.text {
        padding: 3px 10px;
        border: 1px solid var(--cnt-line-strong);
        border-radius: 9999px;
        background: #fff;
        color: var(--cnt-ink);
        font-size: 0.75rem;
        font-weight: 700;
      }
      button.text:hover {
        border-color: var(--cnt-green);
        color: var(--cnt-ink);
      }
    `,
  ],
})
export class AdminCopyButtonComponent {
  private readonly toasts = inject(ToastService);

  @Input({ required: true }) value = '';
  /** What it is, for the toast and the label: "Email", "Phone", "Booking code". */
  @Input() what = 'Text';
  /** Whose it is, for the label: "Copy Gus Guest's phone". */
  @Input() name?: string;
  @Input() icon = 'content_copy';
  @Input({ transform: booleanAttribute }) text = false;

  get label(): string {
    const what = this.what.toLowerCase();
    return this.name ? `Copy ${this.name}'s ${what}` : `Copy ${what} ${this.value}`;
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.value);
      this.toasts.success(`${this.what} copied`);
    } catch {
      this.toasts.error("Couldn't copy");
    }
  }
}
